import { Box, UnstyledButton } from '@mantine/core';
import { ReactNode } from 'react';

export interface TabOption<T = string> {
  label: string;
  icon: ReactNode;
  value: T;
  /** Small status affordance rendered after the label (e.g. an attention dot). */
  indicator?: ReactNode;
}

interface TimelineTabsProps<T = string> {
  tabs: TabOption<T>[];
  activeTab: T;
  onTabChange: (value: T) => void;
  /** `panel`: the Intake hub's bar — white, bottom-bordered, 13px, underline across the tab. */
  variant?: 'default' | 'panel';
}

// Orbit sub-nav canon:
// 36px tall, 8px/12px padding, 4px gap; inline buttons (no chip background).
// Idle = Black at 0.55 opacity, 400 Regular. Hover = full Black, faint
// rgba(0,0,0,0.04) wash. Active = full Black, 600 Semibold, with a 2px Dark
// Green underline directly beneath. No bottom border on the bar.

const ITEM_BASE_STYLE: React.CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  height: 36,
  padding: '0 12px',
  fontSize: 14,
  lineHeight: '20px',
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  transition: 'background-color 0.2s ease, color 0.2s ease',
};

const ACTIVE_UNDERLINE_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: 12,
  right: 12,
  bottom: 0,
  height: 2,
  backgroundColor: 'var(--ev-color-green-dark, #243721)',
};

const BAR_STYLE: React.CSSProperties = { display: 'flex', gap: 4, alignItems: 'stretch' };

const PANEL_BAR_STYLE: React.CSSProperties = {
  display: 'flex',
  gap: 2,
  alignItems: 'stretch',
  padding: '0 12px',
  background: '#fff',
  borderBottom: '1px solid rgba(0, 0, 0, 0.10)',
};

const PANEL_ITEM_STYLE: React.CSSProperties = {
  ...ITEM_BASE_STYLE,
  height: 'auto',
  padding: '11px 12px',
  fontSize: 13,
  lineHeight: '18px',
};

// Spans the tab and sits on the bar's border, as in the prototype.
const PANEL_UNDERLINE_STYLE: React.CSSProperties = { ...ACTIVE_UNDERLINE_STYLE, left: 0, right: 0, bottom: -1 };

export function TimelineTabs<T = string>({ tabs, activeTab, onTabChange, variant = 'default' }: TimelineTabsProps<T>) {
  const isPanel = variant === 'panel';
  return (
    <Box style={isPanel ? PANEL_BAR_STYLE : BAR_STYLE}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.value;
        return (
          <UnstyledButton
            key={String(tab.value)}
            onClick={(e) => {
              // Eagerly clear the hover bg before activation. Without this,
              // a click-while-hovering would leave the inline bg = hover gray
              // stuck on the element — onMouseLeave's reset is guarded by
              // !isActive, so once the tab activates the cleanup is skipped.
              e.currentTarget.style.backgroundColor = 'transparent';
              onTabChange(tab.value);
            }}
            style={{
              ...(isPanel ? PANEL_ITEM_STYLE : ITEM_BASE_STYLE),
              color: isActive ? 'var(--mantine-color-black, #000)' : 'rgba(0, 0, 0, 0.55)',
              fontWeight: isActive ? 600 : 400,
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                // The panel bar's hover is colour only.
                if (!isPanel) {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
                }
                e.currentTarget.style.color = 'var(--mantine-color-black, #000)';
              }
            }}
            onMouseLeave={(e) => {
              // Always clear bg on leave, even when active — the bg only
              // belongs to the hover state, never the resting (idle or
              // active) state. Color reset is still guarded by !isActive
              // because active items keep full Black.
              e.currentTarget.style.backgroundColor = 'transparent';
              if (!isActive) {
                e.currentTarget.style.color = 'rgba(0, 0, 0, 0.55)';
              }
            }}
          >
            {tab.icon}
            <span>{tab.label}</span>
            {tab.indicator}
            {isActive && <span style={isPanel ? PANEL_UNDERLINE_STYLE : ACTIVE_UNDERLINE_STYLE} />}
          </UnstyledButton>
        );
      })}
    </Box>
  );
}
